import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SchemaService, DynamicModuleService } from "@/services/APIs";
import {
  getSchemaProperties,
  mergeClearedSchemaFieldsForUpdate,
  prepareInitialValues,
  prepareSubmitValues,
} from "@/lib/dynamicSchemaFormValues";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import PTSNavigationRoutes from "../../utils/navigationRoutes";

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

const AddEmployeePage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params?.id;
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: employeeInfo, isLoading: employeeLoading } = useQuery({
    queryKey: ["dynamic-model", EMPLOYEE_SCHEMA_NAME, id],
    queryFn: () => {
      if (!id) {
        throw new Error("Employee ID is required for edit mode");
      }
      return DynamicModuleService.getItemById(EMPLOYEE_SCHEMA_NAME, id);
    },
    enabled: isEditMode && !!id,
  });

  // Set form initial values when in edit mode
  useEffect(() => {
    if (isEditMode && employeeInfo) {
      const cleaned = removeMetaFields(employeeInfo);
      form.setInitialValues(prepareInitialValues(cleaned));
      form.reset();
    }
  }, [isEditMode, employeeInfo, form]);

  const handleSubmit = async () => {
    try {
      await form.validate();
      const schemaProps = getSchemaProperties(schemaInfo?.json ?? schemaInfo);

      let modifiedValues = prepareSubmitValues(removeMetaFields(form.values));

      if (isEditMode && employeeInfo && Object.keys(schemaProps).length > 0) {
        const cleanedOriginal = removeMetaFields(employeeInfo);
        modifiedValues = mergeClearedSchemaFieldsForUpdate(
          modifiedValues,
          cleanedOriginal,
          schemaProps
        );
      }

      setIsSubmitting(true);

      if (isEditMode && id && employeeInfo) {
        const originalForCompare = prepareSubmitValues(
          removeMetaFields(employeeInfo)
        );
        if (!checkFormChanges(modifiedValues, originalForCompare, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          EMPLOYEE_SCHEMA_NAME,
          id,
          modifiedValues
        );
        if (response) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ["dynamic-model", EMPLOYEE_SCHEMA_NAME, id],
            }),
            queryClient.invalidateQueries({
              queryKey: ["moduleData", EMPLOYEE_SCHEMA_NAME],
            }),
          ]);
          navigate(PTSNavigationRoutes.EMPLOYEE);
        }
      } else {
        const response = await DynamicModuleService.createItem(
          EMPLOYEE_SCHEMA_NAME,
          modifiedValues
        );
        if (response) {
          await queryClient.invalidateQueries({
            queryKey: ["moduleData", EMPLOYEE_SCHEMA_NAME],
          });
          navigate(PTSNavigationRoutes.EMPLOYEE);
        }
      }
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "create"} employee`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(PTSNavigationRoutes.EMPLOYEE);
  };

  if (schemaLoading || (isEditMode && employeeLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !employeeInfo) {
    return <NotFound entityName="Employee" />;
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
              {isEditMode ? "Edit Employee" : "Add New Employee"}
            </h1>
            <p className="text-sm text-[#627384]">
              {isEditMode
                ? "Update the employee information below"
                : "Fill in the employee information below"}
            </p>
          </div>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Save className="h-4 w-4" />
          {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
        </Button>
      </div>

      {/* Employee Form */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-6">
          <SchemaFormView
            form={form}
            schemaJson={schemaInfo?.json ?? schemaInfo}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEmployeePage;
